import React, { useState } from "react";
import { MapPin, Phone, Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useContent } from "../context/ContentContext";
import PageHero from "../components/PageHero";
import ExternalLinks from "../components/ExternalLinks";

export default function Contact() {
  const { content } = useContent();
  const c = content?.contact || {};
  const ph = content?.page_heroes?.contact || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! Our team will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <PageHero
        eyebrow={ph.eyebrow || "Contact"}
        title={ph.title || "Get in touch"}
        subtitle={ph.subtitle || "We'd love to help plan your stay in the Mutare mountains."}
        image={ph.image || "https://images.unsplash.com/photo-1775023664015-2ec930caa002?q=85&w=1600"}
      />

      <section className="py-20 container-lux grid lg:grid-cols-2 gap-12">
        <div>
          <span className="eyebrow">Reach us</span>
          <h2 className="font-serif text-3xl text-forest mt-2 mb-6">We're here for you</h2>
          <div className="space-y-5">
            <a href={`https://wa.me/${c.whatsapp || "263770000000"}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
              className="flex items-center gap-4 p-4 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors">
              <MessageCircle className="text-[#25D366]" /><div><div className="font-semibold text-forest">WhatsApp us</div><div className="text-sm text-stone-500">Fastest way to reach our concierge</div></div>
            </a>
            <div className="flex items-center gap-4 p-4"><Phone className="text-rose" /><div><div className="font-semibold text-forest">Call</div><div className="text-sm text-stone-500">{c.phone}</div></div></div>
            <div className="flex items-center gap-4 p-4"><Mail className="text-rose" /><div><div className="font-semibold text-forest">Email</div><div className="text-sm text-stone-500">{c.email}</div></div></div>
            <div className="flex items-center gap-4 p-4"><MapPin className="text-rose" /><div><div className="font-semibold text-forest">Location</div><div className="text-sm text-stone-500">{c.address}</div></div></div>
          </div>
          <ExternalLinks className="mt-8" label="Find & book us on" />
          <div className="mt-8 rounded-2xl overflow-hidden border border-stone-200 h-64">
            <iframe title="map" src={c.map_embed || "https://www.google.com/maps?q=Mutare,Zimbabwe&output=embed"} className="w-full h-full" loading="lazy" />
          </div>
        </div>

        <div className="card-lux p-8">
          <h3 className="font-serif text-2xl text-forest mb-6">Send a message</h3>
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <input required placeholder="Full name" data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
            <div className="grid sm:grid-cols-2 gap-4">
              <input required type="email" placeholder="Email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
              <input placeholder="Phone (optional)" data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
            </div>
            <textarea required rows={5} placeholder="Your message" data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
            <button type="submit" disabled={sending} data-testid="contact-submit" className="btn-primary w-full disabled:opacity-60">
              <Send size={16} /> {sending ? "Sending..." : "Send message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
